/**
 * @file useMembership - 会员订阅数据管理 Hook
 *
 * 负责从服务层获取会员数据并转换为 UI 组件所需格式
 */
import {useQuery} from '@tanstack/react-query';
import type {SubscriptionTab} from '../types';
import {queryClient} from '@/utils/queryClient';
import {getSubscriptionPlans, getQuestionAndTips} from '@/services/commerce/membership';
import type {MembershipPlan} from '@/services/commerce/membership/interface';
import {useAuthStore} from '@/store/useAuthStore';

/**
 * 快照 Key Factory
 */
export const queryKeys = {
    plans: ['membership', 'plans'] as const,
    questions: ['membership', 'questions'] as const,
};

/**
 * 会员套餐查询 Hook
 *
 * @param subscriptionType - 订阅类型（月付/季付/年付）
 * @returns 转换后的 UI 组件所需的套餐数据
 */
export const useMemberPlans = () => {
    // 判断用户是否登录
    const isLogin = useAuthStore((state) => state.isLogin);

    const result = useQuery({
        queryKey: queryKeys.plans,
        queryFn: getSubscriptionPlans,
        staleTime: 60 * 60 * 1000, // 60分钟内数据视为新鲜
        retry: 1,
        enabled: isLogin,
    });

    let subscriptionOptions: SubscriptionTab[] | null = null;
    if (result.data) {
        subscriptionOptions = result.data.subscriptionTypes.map((item: MembershipPlan) => ({
            label: item.typeName,
            value: item.subscriptionType,
            tag: item.tag,
        }));
    }

    return {
        ...result,
        subscriptionOptions,
    };
};

// 获取问题列表
export const useMembershipQuestions = () => {
    return useQuery({
        queryKey: queryKeys.questions,
        queryFn: getQuestionAndTips,
        staleTime: 12 * 60 * 60 * 1000, // 12小时
        gcTime: 60 * 60 * 1000, // 1小时
    });
};

// 刷新会员等级数据
export const refreshMembershipData = (): void => {
    void queryClient.invalidateQueries({
        queryKey: queryKeys.plans,
        exact: true,
    });
};
